import { faPencil, faArrowRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { faUndoAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { reduceResolution, wbp } from 'Theme';

import moment from 'moment-timezone';
import DetailTableDashBoard from 'components/UI/DetailTableDashboard/DetailTableDashboard';
import { CommissionsStatus } from '@spike/commissions-action/lib/esm/reducer/CommissionsState';
import { CommissionsReport, SaleCommissionsReport } from '@spike/commission-model';
import { fetchThunk, getReportThunk, resetSaleThunk, updateSaleThunk } from '@spike/commissions-action';
import { showError } from '@spike/notifications-action';
import { useApiClientWrapper, useMarketplace, useRoles } from 'hooks';
import { Option, Period } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import TextOverflow from 'components/UI/TextOverflow';
import { usePaymentsNavOptional } from 'components/Payments/PaymentsNavContext';

import EditCommissionModal from './Edit/EditCommissionModal';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import ResetCommissionModal from './ResetCommissionModal';
import SetupCommissionsDrawer from 'components/UI/SetupCommissionsDrawer/SetupCommissionsDrawer';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(24)}px ${reduceResolution(24)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '24px 24px'
            }
        },
        invoiceCell: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 6
        },
        refundedPill: {
            display: 'inline-flex',
            height: '27px',
            padding: '4px 11px',
            alignItems: 'center',
            gap: '5px',
            borderRadius: '100px',
            background: '#000',
            color: '#FFF',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            cursor: 'pointer',
            border: 'none'
        },
        refundedIcon: {
            width: '13px',
            height: '13px'
        },
        commissionWithAction: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            width: '100%'
        },
        editIcon: {
            color: '#222',
            padding: 4
        }
    })
);

const rowCommissionAmount = (sale: SaleCommissionsReport, overrides: Record<number, string>): string =>
    overrides[sale.id] ?? sale.commissionAmount;

export const CommissionsDetail: FunctionComponent<{ showTitle?: boolean }> = ({ showTitle = true }) => {
    const classes = useStyles();
    const paymentsNav = usePaymentsNavOptional();
    const marketplace = useMarketplace();
    const { isOwner, isManager } = useRoles();
    const isOwnerOrManager = isOwner || isManager;

    const [showSetupCommissionsDrawer, setShowSetupCommissionsDrawer] = useState(false);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const commissionStatus = useSelector<RootState, CommissionsStatus>(state => state.commissions.status);

    const commissionDetail = useSelector<RootState, CommissionsReport | undefined>(state => state.commissions.report);

    const [period, setPeriod] = useState<Period>({
        from: moment().startOf('month'),
        to: moment().endOf('month')
    });

    const [csvFileName, setCsvFileName] = useState<string>(
        `commission_report_${period.from.format('YYYY-MM-DD') || 'all'}_${period.to.format('YYYY-MM-DD') || 'time'}.csv`
    );

    const [staffsToFilter, setStaffsToFilter] = useState<Array<number>>([]);
    const [staffOptions, setStaffOptions] = useState<Array<Option<number>>>([]);

    /** Local overrides until a PATCH commission-line API is wired. */
    const [commissionOverrides, setCommissionOverrides] = useState<Record<number, string>>({});

    const [editSale, setEditSale] = useState<SaleCommissionsReport | null>(null);
    const [saleToReset, setSaleToReset] = useState<number | null>(null);
    const [resettingSaleId, setResettingSaleId] = useState<number | null>(null);
    const resettingSaleIdRef = useRef<number | null>(null);

    const computedTotalCommissions = useMemo(() => {
        if (!commissionDetail?.sales?.length) {
            return commissionDetail?.totalCommissions ?? '0';
        }
        const sum = commissionDetail.sales.reduce(
            (acc, sale) => acc + Number(rowCommissionAmount(sale, commissionOverrides)),
            0
        );
        return sum.toFixed(2);
    }, [commissionDetail?.sales, commissionDetail?.totalCommissions, commissionOverrides]);

    const totals = [
        {
            id: commissionDetail?.bookingCount ?? 0,
            description: 'Total Appointments',
            name: 'appointments'
        },
        {
            id: '$' + (Number(computedTotalCommissions) === 0 ? '0' : Number(computedTotalCommissions).toFixed(2)),
            description: 'Total Commissions',
            name: 'commissions'
        }
    ];

    const headers = [
        { value: 'Date', numeric: false },
        { value: 'Invoice', numeric: false },
        { value: 'Customer', numeric: false },
        { value: 'Staff', numeric: false },
        { value: 'Item', numeric: false },
        { value: 'Price', numeric: true },
        { value: '%', numeric: true },
        { value: 'Commission', numeric: true }
    ];

    const goToRefundedTransactions = useCallback(
        (sale: SaleCommissionsReport) => {
            const refundDate = sale.invoiceRefunds?.[0]?.refundDate;
            const marketplaceId = sale.invoiceRefunds?.[0]?.marketplaceId;
            paymentsNav?.openTransactions('refunded', refundDate, [marketplaceId]);
        },
        [paymentsNav]
    );

    const handleResetCommission = useCallback(
        (saleId: number) => {
            resettingSaleIdRef.current = saleId;
            setResettingSaleId(saleId);
            dispatch(resetSaleThunk(apiClientWrapper, saleId));
        },
        [apiClientWrapper, dispatch]
    );

    const rows = commissionDetail?.sales
        ? commissionDetail.sales.map(sale => {
              const amountStr = rowCommissionAmount(sale, commissionOverrides);
              return [
                  { value: sale.saleDate.format('MM/DD/YYYY'), numeric: false },
                  {
                      value: (
                          <Box className={classes.invoiceCell}>
                              <span>{String(sale.invoice.number)}</span>
                              <InvoiceStatusBadge status={sale.invoice.status} />
                              {sale.hasInvoiceRefund && (
                                  <button
                                      type="button"
                                      className={classes.refundedPill}
                                      onClick={() => goToRefundedTransactions(sale)}
                                  >
                                      <FontAwesomeIcon
                                          icon={faUndoAlt}
                                          className={classes.refundedIcon}
                                      />
                                      Refunded
                                  </button>
                              )}
                          </Box>
                      ),
                      numeric: false
                  },
                  {
                      value: (
                          <TextOverflow
                              text={sale.customerName}
                              length={16}
                          />
                      ),
                      numeric: false
                  },
                  {
                      value: (
                          <TextOverflow
                              text={sale.staff.firstName + ' ' + sale.staff.lastName}
                              length={16}
                          />
                      ),
                      numeric: false
                  },
                  {
                      value: (
                          <TextOverflow
                              text={sale.description}
                              length={16}
                          />
                      ),
                      numeric: false
                  },
                  {
                      value: '$' + Number(sale.amount).toFixed(2),
                      numeric: true
                  },
                  {
                      value: Number(sale.commissionRate * 100).toFixed(2) + '%',
                      numeric: true
                  },
                  {
                      value: (
                          <Box className={classes.commissionWithAction}>
                              <span>{'$' + Number(amountStr).toFixed(2)}</span>
                              <IconButton
                                  aria-label="Edit commission"
                                  className={classes.editIcon}
                                  size="small"
                                  onClick={() =>
                                      setEditSale({
                                          ...sale,
                                          commissionAmount: commissionOverrides[sale.id] ?? sale.commissionAmount
                                      })
                                  }
                              >
                                  <FontAwesomeIcon icon={faPencil} />
                              </IconButton>
                              {sale.locked && (
                                  <IconButton
                                      aria-label={
                                          resettingSaleId === sale.id ? 'Resetting commission' : 'Reset commission'
                                      }
                                      className={classes.editIcon}
                                      size="small"
                                      disabled={resettingSaleId === sale.id}
                                      onClick={() => setSaleToReset(sale.id)}
                                  >
                                      <FontAwesomeIcon icon={faArrowRotateLeft} />
                                  </IconButton>
                              )}
                          </Box>
                      ),
                      numeric: true
                  }
              ];
          })
        : [[{ value: '' }]];

    const csvHeaders = [
        { label: 'Date', key: 'date' },
        { label: 'Invoice', key: 'invoice' },
        { label: 'Customer', key: 'customerName' },
        { label: 'Staff', key: 'staff' },
        { label: 'Item', key: 'service' },
        { label: 'Price', key: 'price' },
        { label: 'Rate', key: 'rate' },
        { label: 'Commission', key: 'commission' }
    ];

    const csvData = commissionDetail?.sales
        ? commissionDetail.sales.map(sale => {
              const amountStr = rowCommissionAmount(sale, commissionOverrides);
              return {
                  date: sale.saleDate.format('MM/DD/YYYY'),
                  invoice: String(sale.invoice.number),
                  customerName: String(sale.customerName).replace(/,/g, ' '),
                  staff: `${String(sale.staff.firstName).replace(/,/g, ' ')} ${String(sale.staff.lastName).replace(
                      /,/g,
                      ' '
                  )}`,
                  service: String(sale.description).replace(/,/g, ' '),
                  price: '$' + (Number(sale.amount) === 0 ? '0' : Number(sale.amount).toFixed(2)),
                  rate:
                      (Number(sale.commissionRate).toFixed(0) === String(sale.commissionRate)
                          ? Number(sale.commissionRate * 100).toFixed(0)
                          : Number(sale.commissionRate * 100).toFixed(2)) + '%',
                  commission: '$' + (Number(amountStr) === 0 ? '0' : Number(amountStr).toFixed(2))
              };
          })
        : [{}];

    const staffOptionsFromSales = useMemo<Array<Option<number>>>(() => {
        const optionsById = new Map<number, Option<number>>();

        (commissionDetail?.sales ?? []).forEach(sale => {
            if (!sale.staff?.id || optionsById.has(sale.staff.id)) {
                return;
            }

            const name = `${sale.staff.firstName} ${sale.staff.lastName}`.replace(/\s+/g, ' ').trim();
            if (name.length === 0) {
                return;
            }

            optionsById.set(sale.staff.id, { id: sale.staff.id, name, description: name });
        });

        return Array.from(optionsById.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [commissionDetail?.sales]);

    useEffect(() => {
        dispatch(fetchThunk(apiClientWrapper));
    }, []);

    useEffect(() => {
        dispatch(getReportThunk(apiClientWrapper, period.from, period.to, staffsToFilter));
    }, [period, staffsToFilter]);

    useEffect(() => {
        if (commissionStatus !== CommissionsStatus.GetReportSuccess) {
            return;
        }

        setStaffOptions(previous => {
            if (staffsToFilter.length === 0) {
                return staffOptionsFromSales;
            }

            return previous.length === 0 ? staffOptionsFromSales : previous;
        });
    }, [commissionStatus, staffOptionsFromSales, staffsToFilter.length]);

    useNonInitialEffect(() => {
        if (commissionStatus === CommissionsStatus.GetUpdateSaleSuccess) {
            dispatch(getReportThunk(apiClientWrapper, period.from, period.to, staffsToFilter));
        }
        if (commissionStatus === CommissionsStatus.ResetSaleSuccess) {
            const saleId = resettingSaleIdRef.current;
            if (saleId != null) {
                setCommissionOverrides(prev => {
                    const next = { ...prev };
                    delete next[saleId];
                    return next;
                });
            }
            resettingSaleIdRef.current = null;
            setResettingSaleId(null);
            setSaleToReset(null);
            dispatch(getReportThunk(apiClientWrapper, period.from, period.to, staffsToFilter));
        }
        if (commissionStatus === CommissionsStatus.Error && resettingSaleIdRef.current != null) {
            resettingSaleIdRef.current = null;
            setResettingSaleId(null);
            setSaleToReset(null);
        }
    }, [commissionStatus]);

    const handleSelectStaff = (staffs: Array<number>, all: boolean) => {
        if (all === true) {
            setStaffsToFilter([]);
        } else {
            setStaffsToFilter(staffs);
        }
    };

    const handleChangePeriod = (dateFilter: Period) => {
        setPeriod({
            from: dateFilter.from,
            to: dateFilter.to
        });
        setCsvFileName(
            `commission_report_${dateFilter.from.format('YYYY-MM-DD') || 'all'}_${
                dateFilter.to.format('YYYY-MM-DD') || 'time'
            }.csv`
        );
    };

    const handleSaveCommission = useCallback(
        async (normalizedAmount: string) => {
            if (!editSale) {
                return;
            }
            try {
                await dispatch(updateSaleThunk(apiClientWrapper, editSale.id, normalizedAmount));
            } catch {
                dispatch(showError('Could not update commission.'));
                throw new Error('save failed');
            }
        },
        [dispatch, editSale]
    );

    return (
        <>
            <Box className={clsx(classes.container)}>
                <DetailTableDashBoard
                    title={showTitle ? 'Commissions Detail' : ''}
                    period={period}
                    totals={totals}
                    staffOptions={staffOptions}
                    headers={headers}
                    rows={rows}
                    loading={commissionStatus === CommissionsStatus.GettingReport}
                    export={true}
                    csv={{
                        data: csvData,
                        headers: csvHeaders,
                        fileName: csvFileName
                    }}
                    onOpenSetupCommissions={() => setShowSetupCommissionsDrawer(true)}
                    onChangePeriod={period => handleChangePeriod(period)}
                    onStaffChange={handleSelectStaff}
                />
            </Box>

            {isOwnerOrManager && showSetupCommissionsDrawer && marketplace && (
                <SetupCommissionsDrawer
                    marketplace={marketplace}
                    onClose={() => setShowSetupCommissionsDrawer(false)}
                />
            )}

            {editSale && (
                <EditCommissionModal
                    sale={editSale}
                    onClose={() => setEditSale(null)}
                    onSave={handleSaveCommission}
                />
            )}

            <ResetCommissionModal
                open={saleToReset != null}
                isResetting={saleToReset != null && resettingSaleId === saleToReset}
                onClose={() => setSaleToReset(null)}
                onConfirm={() => {
                    if (saleToReset != null) {
                        handleResetCommission(saleToReset);
                    }
                }}
            />
        </>
    );
};

export default CommissionsDetail;
